import { buildConversationId } from "@/shared/conversation-utils";

/**
 * Source reference from RAG search
 */
export interface ChatSource {
  id: number;
  sourceType: "summary" | "message";
  sourceId: number;
  metadata: any;
  content: string;
}

function encodeInboxConversationId(conversationId: string): string {
  const parts = conversationId.split(":");
  if (parts.length >= 3) {
    const baseConversationId = `${parts[0]}:${parts[1]}`;
    const userPhoneScope = parts.slice(2).join(":");
    return `${encodeURIComponent(baseConversationId)}:${encodeURIComponent(userPhoneScope)}`;
  }

  return encodeURIComponent(conversationId);
}

/**
 * Generate URL for a source - always links to /messages with the specific message
 */
export function generateSourceUrl(
  source: ChatSource,
  orgId: string
): string | null {
  const metadata = source.metadata;
  const chatId = metadata?.chat_id || metadata?.conversation_id;
  const platform = metadata?.platform;

  if (!chatId || !platform) return null;

  const conversationId = buildConversationId(
    chatId,
    platform,
    metadata?.chat_type,
    metadata?.user_phone
  );

  if (source.sourceType === "summary") {
    const date = metadata?.summary_date || metadata?.date;
    if (!date) return null;
    const dateStr = new Date(date).toISOString().split("T")[0];
    // Link to messages page for the conversation on that date
    return `/org/${orgId}/app/inbox/${encodeInboxConversationId(conversationId)}?date=${dateStr}`;
  } else {
    // Message - link directly to the message
    const timestamp = metadata?.timestamp;
    const messageId = source.sourceId;

    if (!timestamp) return null;

    const date = new Date(timestamp).toISOString().split("T")[0];
    return `/org/${orgId}/app/inbox/${encodeInboxConversationId(
      conversationId
    )}?date=${date}&messageId=${messageId}`;
  }
}

/**
 * Parse message content and extract reference numbers [1], [2], etc.
 * Returns parts of the message with reference markers
 */
export interface MessagePart {
  type: "text" | "reference";
  content: string;
  referenceNumber?: number; // 1-indexed
}

export function parseMessageReferences(content: string): MessagePart[] {
  const parts: MessagePart[] = [];
  const referenceRegex = /\[(\d+)\]/g;

  let lastIndex = 0;
  let match;

  while ((match = referenceRegex.exec(content)) !== null) {
    // Add text before the reference
    if (match.index > lastIndex) {
      const textBefore = content.substring(lastIndex, match.index);
      if (textBefore) {
        parts.push({ type: "text", content: textBefore });
      }
    }

    // Add the reference
    const referenceNumber = parseInt(match[1], 10);
    parts.push({
      type: "reference",
      content: match[0],
      referenceNumber,
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    parts.push({ type: "text", content: content.substring(lastIndex) });
  }

  // If no references found, return the entire content as text
  if (parts.length === 0) {
    parts.push({ type: "text", content });
  }

  return parts;
}

/**
 * Convert message content with references [1], [2] to markdown links [1](url)
 */
export function convertReferencesToUrls(
  content: string,
  sources: ChatSource[] | undefined,
  orgId: string,
  baseUrl: string = window.location.origin
): string {
  if (!sources || sources.length === 0) {
    return content;
  }

  // Replace [1], [2] with [1](url), [2](url)
  const referencePattern = /\[(\d+)\]/g;

  return content.replace(referencePattern, (match, refNumber) => {
    const sourceIndex = parseInt(refNumber, 10) - 1;
    const source = sources[sourceIndex];

    if (!source) {
      return match; // Keep original if source doesn't exist
    }

    const url = generateSourceUrl(source, orgId);
    if (!url) {
      return match; // Keep original if URL can't be generated
    }

    const fullUrl = `${baseUrl}${url}`;
    return `[${refNumber}](${fullUrl})`;
  });
}
